import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import TimeBillingTable from '../components/TimeBilling/TimeBillingTable';
import TimeBillingForm from '../components/TimeBilling/TimeBillingForm';
import UpgradePremiumModal from '../components/Stripe/UpgradePremiumModal';
import '../component-styles/TimeBilling.css';
import API_BASE from '../utils/api.js';
import usePermissions from '../auth/usePermissions';
import { AuthContext } from '../auth/AuthContext';

function getApiBase() {
  return API_BASE;
}

export default function TimeBillingPage() {
  const { projectId } = useParams();
  const { permissions } = usePermissions();
  const { auth } = useContext(AuthContext);
  const isPremium = auth?.plan === 'premium';
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const canAddActivity = permissions.editTimeBillingActivity !== false;

  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  const apiUrl = getApiBase();
  const userId = localStorage.getItem('userId');

  useEffect(() => {
    if (!userId) {
      setLoadingProjects(false);
      return;
    }

    async function loadProjects() {
      try {
        const res = await fetch(`${apiUrl}/api/projects/${encodeURIComponent(userId)}`);
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        const data = await res.json();
        const normalized = (Array.isArray(data) ? data : [])
          .map((raw) => ({
            id: String(raw.projectId || raw.id),
            name: raw.projectNickname || raw.name || raw.title || 'Untitled',
            isActive: raw.isActive ?? true,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setProjects(normalized);
      } catch (err) {
        console.error('Failed to load projects:', err);
        setProjects([]);
      } finally {
        setLoadingProjects(false);
      }
    }

    loadProjects();
  }, [userId, apiUrl]);

  useEffect(() => {
    if (!projectId || !userId) {
      setProject(null);
      return;
    }

    async function fetchProject() {
      try {
        const primaryUrl = `${apiUrl}/api/projects/${encodeURIComponent(
          userId
        )}/${encodeURIComponent(projectId)}`;
        const fallbackUrl = `${apiUrl}/api/projects/${encodeURIComponent(userId)}`;

        let res = await fetch(primaryUrl, { cache: 'no-store' });
        if (res.ok && (res.headers.get('content-type') || '').includes('application/json')) {
          const data = await res.json();
          setProject(data || null);
          return;
        }

        res = await fetch(fallbackUrl, { cache: 'no-store' });
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        const json = await res.json();
        const list = Array.isArray(json) ? json : json.Items || json.items || [];
        const match = (list || []).find(
          (item) =>
            String(item.projectId || item.id) === String(projectId) ||
            (item.projectNickname && encodeURIComponent(item.projectNickname) === projectId) ||
            (item.name && encodeURIComponent(item.name) === projectId)
        );
        setProject(match || null);
      } catch (err) {
        console.error('Failed to load project:', err);
        setProject(null);
      }
    }

    fetchProject();
  }, [projectId, userId, apiUrl]);

  if (!isPremium) {
    return (
      <div className="timebilling-page">
        <div className="timebilling-main">
          <div className="tb-upgrade-gate">
            <div className="tb-upgrade-icon">⏱</div>
            <h2>Time Billing</h2>
            <p>InteriorFolio supports time billing for Premium subscribers. Track billable time, assign entries to projects, and manage workflows directly within the platform.</p>
            <button className="tb-upgrade-btn" onClick={() => setShowUpgradeModal(true)}>
              Upgrade to Premium
            </button>
          </div>
        </div>
        {showUpgradeModal && (
          <UpgradePremiumModal onClose={() => setShowUpgradeModal(false)} />
        )}
      </div>
    );
  }

  const activeProjects = projects.filter((p) => p.isActive);
  const inactiveProjects = projects.filter((p) => !p.isActive);

  return (
    <div className="timebilling-page">
      <div className="timebilling-main">
        <header className="timebilling-header">
          <label className="tb-project-picker">
            <span>Project</span>
            <div className="tb-select-wrap">
              <select
                className="tb-form-select"
                value={projectId || ''}
                onChange={(e) =>
                  e.target.value && navigate(`/timebilling/${encodeURIComponent(e.target.value)}`)
                }
              >
                <option value="" disabled>
                  {loadingProjects ? 'Loading projects...' : 'Select a project...'}
                </option>
                {inactiveProjects.length > 0 ? (
                  <>
                    <optgroup label="Active">
                      {activeProjects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Inactive">
                      {inactiveProjects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </optgroup>
                  </>
                ) : (
                  activeProjects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))
                )}
              </select>
            </div>
          </label>
          {canAddActivity && (
            <button
              className="add-btn"
              disabled={!projectId}
              onClick={() =>
                document.dispatchEvent(
                  new CustomEvent('open-timebilling-form', {
                    detail: { mode: 'create' },
                  })
                )
              }
            >
              + Add New
            </button>
          )}
        </header>
        {projectId ? (
          <TimeBillingTable projectId={projectId} />
        ) : (
          <div className="empty-state">Select a project to view time entries.</div>
        )}
        <TimeBillingForm projectId={projectId} project={project} />
      </div>
    </div>
  );
}
